/* eslint-disable no-param-reassign */

// Reference:
// https://github.com/shuding/cobe/pull/52/files

import { useSpring } from '@react-spring/web';
import clsx from 'clsx';
import createGlobe from 'cobe';
import { useEffect, useRef } from 'react';

type CanvasRef = React.RefObject<HTMLCanvasElement>;

interface IGlobe {
  className?: string;
  locations: {
    lat: number;
    lng: number;
  }[];
}

export const Globe = ({ className, locations }: IGlobe) => {
  const canvasRef: CanvasRef = useRef<HTMLCanvasElement>(null);
  const pointerInteracting = useRef<number | null>();
  const pointerInteractionMovement = useRef(0);
  const [, api] = useSpring(() => ({
    r: 0,
    config: {
      mass: 1,
      tension: 280,
      friction: 40,
      precision: 0.001,
    },
  }));

  useEffect(() => {
    if (!canvasRef.current) return;
    let phi = 0;
    let width = 0;
    const onResize = () => {
      if (!canvasRef.current) return;
      width = canvasRef.current.offsetWidth;
      canvasRef.current.style.height = `${width}px`;
    };

    window.addEventListener('resize', onResize);
    onResize();
    const globe = createGlobe(canvasRef.current, {
      devicePixelRatio: 2,
      width: width * 2,
      height: width * 2,
      phi: 0,
      theta: 0.3,
      dark: 1,
      diffuse: 2,
      mapSamples: 16000,
      mapBrightness: 4,
      baseColor: [1, 1, 1],
      markerColor: [254, 0 / 10, 165 / 10],
      glowColor: [47 / 170, 162 / 170, 255 / 170],
      markers: locations.map(({ lat, lng }) => ({
        location: [lat, lng],
        size: 0.05,
      })),
      onRender: (state) => {
        // This prevents rotation while dragging
        if (!pointerInteracting.current) {
          // Called on every animation frame.
          // `state` will be an empty object, return updated params.
          phi += 0.005;
        }
        state.phi = phi + pointerInteractionMovement.current / 200;
        state.width = width * 2;
        state.height = width * 2;
      },
    });

    // Gracefully load the globe
    setTimeout(() => {
      if (!canvasRef.current) return;
      canvasRef.current.style.opacity = '1';
    }, 100);

    // eslint-disable-next-line consistent-return
    return () => globe.destroy();
  }, []);

  if (!locations.length) return null;

  return (
    <div
      className={clsx(
        'relative m-auto aspect-square w-full max-w-[600px]',
        className
      )}
    >
      <canvas
        ref={canvasRef}
        onPointerDown={(e) => {
          if (!canvasRef.current) return;
          pointerInteracting.current =
            e.clientX - pointerInteractionMovement.current;
          canvasRef.current.style.cursor = 'grabbing';
        }}
        onPointerUp={() => {
          if (!canvasRef.current) return;
          pointerInteracting.current = null;
          canvasRef.current.style.cursor = 'grab';
        }}
        onPointerOut={() => {
          if (!canvasRef.current) return;
          pointerInteracting.current = null;
          canvasRef.current.style.cursor = 'grab';
        }}
        onMouseMove={(e) => {
          if (!pointerInteracting.current) return;
          const delta = e.clientX - pointerInteracting.current;
          pointerInteractionMovement.current = delta;
          api.start({
            r: delta / 200,
          });
        }}
        onTouchMove={(e) => {
          if (!pointerInteracting.current) return;
          if (e.touches[0]) {
            const delta = e.touches[0].clientX - pointerInteracting.current;
            pointerInteractionMovement.current = delta;
            api.start({
              r: delta / 100,
            });
          }
        }}
        style={{
          width: '100%',
          height: '100%',
          cursor: 'grab',
          contain: 'layout paint size',
          opacity: 0,
          transition: 'opacity 1s ease',
        }}
      />
    </div>
  );
};
